import { ru, type StringTable } from './ru';
import { uz } from './uz';
import { en } from './en';

export type Lang = 'ru' | 'uz' | 'en';
export const LANGS: Lang[] = ['ru', 'uz', 'en'];

const TABLES: Record<Lang, StringTable> = { ru, uz, en };

export type StringKey = { [K in keyof StringTable]: StringTable[K] extends string ? K : never }[keyof StringTable];
export type ListKey = { [K in keyof StringTable]: StringTable[K] extends readonly string[] ? K : never }[keyof StringTable];

/** A single string, with `{name}` placeholders substituted. */
export function t(lang: Lang, key: StringKey, vars?: Record<string, string | number>): string {
  let s: string = TABLES[lang][key];
  if (vars) {
    for (const [k, v] of Object.entries(vars)) s = s.replace(`{${k}}`, String(v));
  }
  return s;
}

/** A list of strings. */
export function tl(lang: Lang, key: ListKey): readonly string[] {
  return TABLES[lang][key];
}

/** Eye state that changes how a correction is named (aphakic glasses, contact lenses). */
export interface CorrectionContext {
  aphakia: boolean;
}

/** Correction label / description by id. Toric ids compose the base text; aphakia has its own wording. */
export function correctionText(lang: Lang, id: string, part: 'label' | 'desc', ctx?: CorrectionContext): string {
  const table = TABLES[lang] as unknown as Record<string, string | undefined>;
  if (ctx?.aphakia) {
    const aphakic = table[`corr.${id}_aphakic.${part}`];
    if (aphakic !== undefined) return aphakic;
  }
  const direct = table[`corr.${id}.${part}`];
  if (direct !== undefined) return direct;
  if (id.endsWith('_toric')) {
    const base = id.slice(0, -'_toric'.length);
    const baseText = table[`corr.${base}.${part}`];
    if (baseText !== undefined) return part === 'desc' ? `${baseText} ${table['corr.toric.desc'] ?? ''}` : baseText;
  }
  return id;
}

/** Scene tile name by id. */
export function sceneName(lang: Lang, id: string): string {
  const table = TABLES[lang] as unknown as Record<string, string | undefined>;
  return table[`scene.${id}`] ?? id;
}

/** Format dioptres with sign and two decimals, e.g. "−2.50 D". */
export function formatD(d: number): string {
  return `${formatSigned(d)} D`;
}

/** Signed value with two decimals and a typographic minus, e.g. "−2.50", "+1.00", "0.00". */
export function formatSigned(d: number): string {
  // Round half away from zero, as refraction is written clinically (−0.875 → −0.88).
  const r = (Math.sign(d) * Math.round(Math.abs(d) * 100 + 1e-9)) / 100;
  const sign = r < 0 ? '−' : r > 0 ? '+' : '';
  return `${sign}${Math.abs(r).toFixed(2)}`;
}
